<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.target") }}</template>

    <input-text-editor
      :label="$t('labels.description')"
      :value="ctx.target.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div v-if="features.indicator">
      <h3 class="label mt-0">{{ $t("objectNames.indicator", 4) }}</h3>
      <indicator-list :target-id="ctx.target.id" />
    </div>

    <div v-if="features.momentum">
      <div class="line-docanchor">
        <label class="label">{{ $t("labels.momentumActive") }}</label>
        <documentation-anchor :object="ctx.target" prop="momentumActive" />
      </div>
      <input-switch
        :value="ctx.target.momentumActive"
        @update:value="updateMomentum($event)"
      />
      <button-item
        v-if="ctx.target.momentumActive"
        class="mt-2"
        @click="emit('select', momentum)"
      >
        {{ $t("labels.editX", [$t("objectNames.momentum")]) }}
      </button-item>
    </div>

    <div v-if="features.stakeholder">
      <h3 class="label mt-0">{{ $t("objectNames.stakeholder", 4) }}</h3>
      <stakeholder-list
        item-type="target"
        :item-id="ctx.target.id"
        :selected-stakeholder-ids="[]"
        :select-enabled="false"
        @select="emit('select', $event)"
      />
    </div>

    <div class="column gap-4" v-if="features.design">
      <input-switch
        :value="ctx.treeNode?.visible"
        @update:value="ctx.updateNodeKey('visible', $event)"
      >
        {{ $t("labels.visible") }}
      </input-switch>

      <template v-if="ctx.treeNode && ctx.treeNode.displayPosition === 'scene' && ctx.sceneNode">
        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.valueStyle") }}</legend>
          <div class="column gap-2">
            <input-switch
              :value="ctx.sceneNode.data.valueVisible"
              @update:value="ctx.updateNodeDataKey('valueVisible', $event)"
            >
              {{ $t("labels.elementValueVisible") }}
            </input-switch>
            <input-switch
              :value="ctx.sceneNode.data.barVisible"
              @update:value="ctx.updateNodeDataKey('barVisible', $event)"
            >
              {{ $t("labels.elementBarVisible") }}
            </input-switch>
          </div>
          <input-switch
            v-if="ctx.sceneNode.data.barVisible"
            :value="ctx.sceneNode.data.barWidthLimited"
            @update:value="ctx.updateNodeDataKey('barWidthLimited', $event)"
          >
            {{ $t("labels.barWidthLimited") }}
          </input-switch>
        </fieldset>

        <!-- hide title -->
        <input-switch
          :value="ctx.sceneNode.data.headlineHidden"
          :label="$t('labels.hideElementTitle')"
          :text-on="$t('labels.yes')"
          :text-off="$t('labels.no')"
          @update:value="ctx.updateNodeDataKey('headlineHidden', $event)"
        />
      </template>
    </div>

    <button
      type="button"
      class="button button_secondary mv-2"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteTarget", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed } from "vue";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import ButtonItem from "@core/input/ButtonItem.vue";
import InputTextEditor from '@core/input/TextEditor.vue';
import InputSwitch from '@core/input/InputSwitch.vue';
import IndicatorList from '../indicator/IndicatorList.vue';
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";

import { useTarget } from "@editor/items/target/useTarget";
import { useFeatures } from "@editor/store/features.store";
import { useMomentumStore } from "../momentum/momentums.store";

import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  targetId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"momentum" | "stakeholder"> }): void;
}>();

const ctx = useTarget(() => props.targetId);

const features = useFeatures();

// =====================
// MOMENTUM

const momentum = computed(() => {
  return useMomentumStore().byTarget[props.targetId];
});

async function updateMomentum(value: boolean) {
  // create a momentum for the target if there isn't one.
  if (value === true && momentum.value == null) {
    await useMomentumStore().create({
      elementType: "target",
      elementId: props.targetId
    });
  }
  ctx.updateKey("momentumActive", value);
}
</script>